import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import './Admin.css';

interface StorageProvider {
  id: number;
  name: string;
  displayName?: string;
  providerType: string;
  endpointFormat: string;
  regionHint?: string;
  authType: string;
  signatureVersion?: string;
  isActive: boolean;
  bucket?: string;
  libraryBucket?: string;
  publicBaseUrl?: string;
  forcePathStyle?: boolean;
  accountId?: string;
  serviceUrl?: string;
  hasCredentials?: boolean;
  accessKeyHint?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface StorageProviderFormData {
  name: string;
  displayName: string;
  providerType: string;
  endpointFormat: string;
  regionHint: string;
  authType: string;
  signatureVersion: string;
  isActive: boolean;
  bucket: string;
  libraryBucket: string;
  publicBaseUrl: string;
  forcePathStyle: boolean;
  accountId: string;
  serviceUrl: string;
  accessKey: string;
  secretKey: string;
}

export function StorageProviders() {
  const { accessToken } = useAuthStore();
  const [providers, setProviders] = useState<StorageProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<StorageProvider | null>(null);
  const emptyForm = (): StorageProviderFormData => ({
    name: '',
    displayName: '',
    providerType: 'minio',
    endpointFormat: 'http://127.0.0.1:9100',
    regionHint: 'us-east-1',
    authType: 'access_key_secret_key',
    signatureVersion: 'sigv4',
    isActive: true,
    bucket: 'tween-assets',
    libraryBucket: 'tween-libs',
    publicBaseUrl: '',
    forcePathStyle: true,
    accountId: '',
    serviceUrl: '',
    accessKey: '',
    secretKey: '',
  });

  const [formData, setFormData] = useState<StorageProviderFormData>(emptyForm);

  const providerTypes = [
    { value: 's3', label: 'Amazon S3' },
    { value: 'minio', label: 'MinIO' },
    { value: 'gcs', label: 'Google Cloud Storage' },
    { value: 'azure', label: 'Azure Blob' },
    { value: 'custom', label: 'Custom (S3-compatible)' },
  ];

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/storage-providers', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch storage providers');
      const data = await res.json();
      setProviders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load storage providers');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleOpenModal = (provider?: StorageProvider) => {
    if (provider) {
      setEditingProvider(provider);
      setFormData({
        name: provider.name,
        displayName: provider.displayName || '',
        providerType: provider.providerType,
        endpointFormat: provider.endpointFormat || '',
        regionHint: provider.regionHint || '',
        authType: provider.authType || 'access_key_secret_key',
        signatureVersion: provider.signatureVersion || 'sigv4',
        isActive: provider.isActive,
        bucket: provider.bucket || '',
        libraryBucket: provider.libraryBucket || '',
        publicBaseUrl: provider.publicBaseUrl || '',
        forcePathStyle: provider.forcePathStyle ?? provider.providerType === 'minio',
        accountId: provider.accountId || '',
        serviceUrl: provider.serviceUrl || '',
        accessKey: '',
        secretKey: '',
      });
    } else {
      setEditingProvider(null);
      setFormData(emptyForm());
    }
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProvider(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setActionLoading(editingProvider?.id ?? 0);

    try {
      const url = editingProvider
        ? `/api/admin/storage-providers/${editingProvider.id}`
        : '/api/admin/storage-providers';
      const method = editingProvider ? 'PUT' : 'POST';

      const body = {
        name: formData.name,
        displayName: formData.displayName || null,
        providerType: formData.providerType,
        endpointFormat: formData.endpointFormat,
        regionHint: formData.regionHint || null,
        authType: formData.authType,
        signatureVersion: formData.signatureVersion || null,
        isActive: formData.isActive,
        bucket: formData.bucket || null,
        libraryBucket: formData.libraryBucket || null,
        publicBaseUrl: formData.publicBaseUrl || null,
        forcePathStyle: formData.forcePathStyle,
        accountId: formData.accountId || null,
        serviceUrl: formData.serviceUrl || null,
        accessKey: formData.accessKey || null,
        secretKey: formData.secretKey || null,
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Failed to save storage provider');
      }

      await fetchProviders();
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save storage provider');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (provider: StorageProvider) => {
    setActionLoading(provider.id);
    setError('');

    try {
      const res = await fetch(`/api/admin/storage-providers/${provider.id}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Failed to toggle provider status');
      }
      await fetchProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle provider status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (provider: StorageProvider) => {
    if (!confirm(`"${provider.name}" storage provider'ı silmek istediğinize emin misiniz?`)) return;

    setActionLoading(provider.id);
    setError('');

    try {
      const res = await fetch(`/api/admin/storage-providers/${provider.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Failed to delete storage provider');
      }
      await fetchProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete storage provider');
    } finally {
      setActionLoading(null);
    }
  };

  const getConfigPreview = (p: StorageProvider): string => {
    const parts: string[] = [];
    if (p.endpointFormat) parts.push(p.endpointFormat);
    if (p.bucket) parts.push(`bucket=${p.bucket}`);
    if (p.libraryBucket) parts.push(`library=${p.libraryBucket}`);
    if (p.regionHint) parts.push(`region=${p.regionHint}`);
    parts.push(p.hasCredentials ? 'creds=ok' : 'creds=missing');
    return parts.length > 0 ? parts.join(' | ') : '-';
  };

  const getProviderTypeLabel = (type: string): string =>
    providerTypes.find((t) => t.value === type)?.label || type;

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Storage Providers</h1>
          <p>Depolama sağlayıcılarını yönetin ve yapılandırın</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
          + Yeni Storage Provider Ekle
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Storage Providers ({providers.length})</h2>
        </div>

        {providers.length === 0 ? (
          <div className="admin-empty-state">
            <p>Henüz storage provider tanımlanmamış.</p>
            <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
              İlk Storage Provider'ı Ekle
            </button>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Tip</th>
                  <th>Endpoint / Bucket</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <tr key={provider.id}>
                    <td>
                      <div className="provider-info">
                        <span className="provider-name">{provider.displayName || provider.name}</span>
                        <span className="provider-description">{provider.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="provider-type-badge">{getProviderTypeLabel(provider.providerType)}</span>
                    </td>
                    <td>
                      <code style={{ fontSize: '0.85em' }}>
                        {getConfigPreview(provider).length > 80
                          ? getConfigPreview(provider).slice(0, 77) + '...'
                          : getConfigPreview(provider)}
                      </code>
                    </td>
                    <td>
                      <button
                        className={`admin-toggle-btn ${provider.isActive ? 'active' : ''}`}
                        onClick={() => handleToggleActive(provider)}
                        disabled={actionLoading === provider.id}
                        title={provider.isActive ? 'Deaktif Et' : 'Aktif Et'}
                      >
                        {provider.isActive ? '✓ Aktif' : '✗ Pasif'}
                      </button>
                    </td>
                    <td>
                      <div className="admin-action-buttons">
                        <button
                          className="admin-edit-btn"
                          onClick={() => handleOpenModal(provider)}
                          disabled={actionLoading === provider.id}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          className="admin-delete-btn"
                          onClick={() => handleDelete(provider)}
                          disabled={actionLoading === provider.id}
                          title="Sil"
                        >
                          {actionLoading === provider.id ? '...' : '🗑'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingProvider ? 'Storage Provider Düzenle' : 'Yeni Storage Provider Ekle'}</h2>
              <button className="admin-modal-close" onClick={handleCloseModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Teknik Ad *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="örn: s3-production"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Görünen Ad
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="örn: S3 Production"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Tip *</label>
                <select
                  className="admin-form-select"
                  value={formData.providerType}
                  onChange={(e) => setFormData({ ...formData, providerType: e.target.value })}
                  required
                >
                  {providerTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Endpoint *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.endpointFormat}
                    onChange={(e) => setFormData({ ...formData, endpointFormat: e.target.value })}
                    required
                    placeholder="örn: http://127.0.0.1:9100 (MinIO) veya https://s3.{region}.amazonaws.com"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Region
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.regionHint}
                    onChange={(e) => setFormData({ ...formData, regionHint: e.target.value })}
                    placeholder="örn: us-east-1"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Bucket *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.bucket}
                    onChange={(e) => setFormData({ ...formData, bucket: e.target.value })}
                    required
                    placeholder="tween-assets"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Library bucket
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.libraryBucket}
                    onChange={(e) => setFormData({ ...formData, libraryBucket: e.target.value })}
                    placeholder="tween-libs"
                  />
                </label>
                <p className="admin-muted-text">
                  MinIO konsolunda tween-libs adıyla oluştur. Paketler oradan listelenir; kullanıcı dosyaları tween-assets’te kalır.
                </p>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Access Key {editingProvider ? '(boş = değiştirme)' : '*'}
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.accessKey}
                    onChange={(e) => setFormData({ ...formData, accessKey: e.target.value })}
                    required={!editingProvider}
                    autoComplete="off"
                    placeholder={editingProvider?.accessKeyHint || 'MINIO_ROOT_USER'}
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Secret Key {editingProvider ? '(boş = değiştirme)' : '*'}
                  <input
                    type="password"
                    className="admin-form-input"
                    value={formData.secretKey}
                    onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
                    required={!editingProvider}
                    autoComplete="new-password"
                    placeholder={editingProvider?.hasCredentials ? '••••••••' : 'MINIO_ROOT_PASSWORD'}
                  />
                </label>
              </div>

              {formData.providerType === 's3' && formData.name.toLowerCase().includes('r2') && (
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Cloudflare Account ID
                    <input
                      type="text"
                      className="admin-form-input"
                      value={formData.accountId}
                      onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                      placeholder="R2 account id"
                    />
                  </label>
                </div>
              )}

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Public Base URL
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.publicBaseUrl}
                    onChange={(e) => setFormData({ ...formData, publicBaseUrl: e.target.value })}
                    placeholder="CDN veya public URL (opsiyonel)"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.forcePathStyle}
                    onChange={(e) => setFormData({ ...formData, forcePathStyle: e.target.checked })}
                  />
                  <span>Force path style (MinIO vb.)</span>
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Aktif</span>
                </label>
              </div>

              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={handleCloseModal}>
                  İptal
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={actionLoading !== null}>
                  {actionLoading !== null ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StorageProviders;
